<template>
  <div class="student-picker">
    <div class="picker-input-wrap">
      <van-icon name="search" color="#999" size="16" />
      <input
        type="text"
        class="picker-input"
        :value="displayName"
        :placeholder="placeholder"
        readonly
        @click="showPicker = true"
      />
      <van-icon 
        v-if="displayName" 
        name="cross" 
        color="#999" 
        size="14" 
        @click.stop="clearSelection" 
      />
      <van-icon v-else name="arrow-down" color="#999" size="14" />
    </div>

    <van-popup v-model:show="showPicker" position="bottom" round :style="popupStyle" @open="getTabbarHeight">
      <div class="picker-panel">
        <div class="picker-header">
          <span class="picker-title">{{ title }}</span>
          <van-icon name="cross" @click="showPicker = false" />
        </div>
        <van-search
          v-model="searchText"
          placeholder="搜索学生姓名"
          shape="round"
          @search="searchStudents"
          @blur="searchStudents"
        />
        <div class="student-list">
          <div
            v-for="student in studentList"
            :key="student.studentId"
            class="student-item"
            :class="{ selected: selectedId === student.studentId }"
            @click="selectStudent(student)"
          >
            <van-icon name="user-o" size="20" />
            <span class="student-name">{{ student.studentName }}</span>
            <van-icon v-if="selectedId === student.studentId" name="success" color="#1677ff" />
          </div>
          <van-empty v-if="studentList.length === 0 && !searching" description="暂无学生" />
          <div v-if="searching" class="searching">搜索中...</div>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { getStudentList } from '@/api/student'

const props = defineProps({
  modelValue: {
    type: Object,
    default: null
  },
  placeholder: {
    type: String,
    default: '请选择学员'
  },
  title: {
    type: String,
    default: '选择学员'
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const showPicker = ref(false)
const searchText = ref('')
const studentList = ref([])
const searching = ref(false)
const selectedId = ref(null)
const selectedName = ref('')
const tabbarHeight = ref(56)

const displayName = computed(() => {
  return selectedName.value || ''
})

const popupStyle = computed(() => {
  return {
    height: '400px',
    bottom: `${tabbarHeight.value}px`
  }
})

function getTabbarHeight() {
  const tabbar = document.querySelector('.van-tabbar')
  if (tabbar) {
    tabbarHeight.value = tabbar.offsetHeight || 56
  }
}

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    selectedId.value = newVal.studentId
    selectedName.value = newVal.studentName
  } else {
    selectedId.value = null
    selectedName.value = ''
  }
}, { immediate: true })

async function searchStudents() {
  searching.value = true
  try {
    const students = await getStudentList({ studentName: searchText.value })
    studentList.value = students
  } catch (error) {
    console.error('搜索学生失败:', error)
    studentList.value = []
  } finally {
    searching.value = false
  }
}

function selectStudent(student) {
  selectedId.value = student.studentId
  selectedName.value = student.studentName
  showPicker.value = false
  const result = {
    studentId: student.studentId,
    studentName: student.studentName
  }
  emit('update:modelValue', result)
  emit('change', result)
}

function clearSelection() {
  selectedId.value = null
  selectedName.value = ''
  emit('update:modelValue', null)
  emit('change', null)
}
</script>

<style scoped>
.student-picker {
  width: 100%;
}

.picker-input-wrap {
  display: flex;
  align-items: center;
  background-color: #f5f7fa;
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
}

.picker-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: #333;
  margin: 0 8px;
}

.picker-input::placeholder {
  color: #999;
}

.picker-panel {
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.picker-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.picker-panel :deep(.van-search) {
  margin: 12px 16px;
  flex-shrink: 0;
}

.picker-panel :deep(.van-search__content) {
  background-color: #f5f7fa;
}

.student-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
}

.student-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 8px;
  background-color: #f5f7fa;
  cursor: pointer;
  transition: all 0.2s ease;
}

.student-item.selected {
  background-color: #e6f4ff;
}

.student-item:active {
  transform: scale(0.98);
}

.student-name {
  flex: 1;
  margin-left: 10px;
  font-size: 14px;
  color: #333;
}

.searching {
  text-align: center;
  padding: 20px;
  font-size: 14px;
  color: #999;
}
</style>
